import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useLazyLoad } from "../hooks/useLazyLoad";
import { AdminCertificate } from "../types/admin";
import { certificatesApi } from "../services/api";
import "../css/certificates.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5167/api";

const getDownloadUrl = (id: string | number) =>
  `${API_BASE_URL}/certificates/${id}/download`;

const getIssuerLogoUrl = (id: string | number) =>
  `${API_BASE_URL}/certificates/${id}/issuer-logo`;

const isPdf = (fileUrl: string | null): boolean => {
  if (!fileUrl) return false;
  return fileUrl.toLowerCase().endsWith(".pdf");
};

const Certificates: React.FC = () => {
  const { lang } = useLanguage();
  const [sectionRef, isVisible] = useLazyLoad("300px");
  const [certificates, setCertificates] = useState<AdminCertificate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxIsPdf, setLightboxIsPdf] = useState(false);
  const [visibleRows, setVisibleRows] = useState(2);
  const gridRef = useRef<HTMLDivElement>(null);

  const COLS_DESKTOP = 3;
  const COLS_TABLET = 2;
  const COLS_MOBILE = 1;
  const ROWS_STEP = 2;

  const getColumns = useCallback(() => {
    const w = window.innerWidth;
    if (w <= 768) return COLS_MOBILE;
    if (w <= 1200) return COLS_TABLET;
    return COLS_DESKTOP;
  }, []);

  const visibleCount = visibleRows * getColumns();
  const totalRows = Math.ceil(certificates.length / getColumns());
  const hasMore = visibleRows < totalRows;

  useEffect(() => {
    if (!isVisible) return;
    const load = async () => {
      try {
        const response = await certificatesApi.getAll({ pageSize: 100 });
        if (response.success && response.data) {
          const sorted = [...response.data].sort((a, b) => {
            const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
          setCertificates(sorted);
        }
      } catch {
        // silently fail
      } finally {
        setLoaded(true);
      }
    };
    void load();
  }, [isVisible]);

  if (!isVisible) {
    return (
      <section
        className="certificates lazy-section"
        id="certificates"
        ref={sectionRef as React.RefObject<HTMLElement>}
      />
    );
  }

  if (!loaded || certificates.length === 0) {
    return (
      <section
        className="certificates lazy-section"
        id="certificates"
        ref={sectionRef as React.RefObject<HTMLElement>}
      />
    );
  }

  const formatDate = (iso: string | null): string => {
    if (!iso) return "";
    const date = new Date(iso);
    return date.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const openCredential = (url: string) => {
    const fullUrl = url.startsWith("http") ? url : `https://${url}`;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const downloadFile = (id: string | number) => {
    window.open(getDownloadUrl(id), "_blank", "noopener,noreferrer");
  };

  const openLightbox = (cert: AdminCertificate) => {
    const url = getDownloadUrl(cert.id);
    setLightboxIsPdf(isPdf(cert.fileUrl));
    setLightboxUrl(url);
  };

  return (
    <section
      className={`certificates lazy-section${loaded ? " lazy-section--visible" : ""}`}
      id="certificates"
      ref={sectionRef as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <h2 className="section-title">
          {lang === "pt" ? "Certificações" : "Certifications"}
        </h2>
        <p className="section-subtitle">
          {lang === "pt"
            ? "Certificados e qualificações profissionais"
            : "Professional certificates and qualifications"}
        </p>

        <div className="certificates-grid" ref={gridRef}>
          {certificates.slice(0, visibleCount).map((cert) => {
            const description =
              lang === "pt" ? cert.descriptionPt : cert.descriptionEn;
            const fileDownloadUrl = getDownloadUrl(cert.id);
            const fileIsPdf = isPdf(cert.fileUrl);

            return (
              <div key={cert.id} className="cert-card">
                {/* File preview */}
                {cert.fileUrl ? (
                  <div
                    className="cert-card-preview"
                    onClick={() => openLightbox(cert)}
                  >
                    {fileIsPdf ? (
                      <div className="cert-card-pdf-wrapper">
                        <object
                          data={`${fileDownloadUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          type="application/pdf"
                          className="cert-card-pdf"
                        >
                          <div className="cert-card-pdf-fallback">
                            <i className="fa-solid fa-file-pdf" />
                            <span>PDF</span>
                          </div>
                        </object>
                        <div className="cert-card-preview-overlay">
                          <i className="fa-solid fa-expand" />
                        </div>
                      </div>
                    ) : (
                      <div className="cert-card-image-wrapper">
                        <img
                          src={fileDownloadUrl}
                          alt={cert.name}
                          className="cert-card-image"
                          loading="lazy"
                        />
                        <div className="cert-card-preview-overlay">
                          <i className="fa-solid fa-expand" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="cert-card-icon">
                    <i className="fa-solid fa-certificate" />
                  </div>
                )}

                <div className="cert-card-body">
                  <div className="cert-card-body-header">
                    <h3 className="cert-card-name">{cert.name}</h3>
                    <div className="cert-card-issuer-badge-wrapper">
                      <div className="cert-card-issuer-badge">
                        <i className="fa-solid fa-award cert-card-stamp-icon" />
                        {cert.issuerLogoUrl ? (
                          <img
                            src={getIssuerLogoUrl(cert.id)}
                            alt={cert.issuerName}
                            className="cert-card-issuer-badge-img"
                            loading="lazy"
                          />
                        ) : (
                          <span className="cert-card-issuer-badge-fallback">
                            {cert.issuerName.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="cert-card-issuer-badge-name">{cert.issuerName}</span>
                    </div>
                  </div>

                  <div className="cert-card-dates">
                    <span className="cert-card-date">
                      <i className="fa-regular fa-calendar" />
                      {formatDate(cert.issueDate)}
                    </span>
                    {cert.expirationDate && (
                      <span className="cert-card-date cert-card-expiry">
                        <i className="fa-regular fa-clock" />
                        {lang === "pt" ? "Exp: " : "Exp: "}
                        {formatDate(cert.expirationDate)}
                      </span>
                    )}
                  </div>

                  {description && (
                    <p className="cert-card-description">{description}</p>
                  )}

                  {/* {cert.credentialId && cert.credentialId !== "0" && (
                    <span className="cert-card-credential-id">
                      ID: {cert.credentialId}
                    </span>
                  )} */}

                  <div className="cert-card-actions">
                    {cert.credentialUrl && (
                      <button
                        className="cert-btn cert-btn-credential"
                        onClick={() => openCredential(cert.credentialUrl)}
                      >
                        <i className="fa-solid fa-up-right-from-square" />
                        <span>
                          {lang === "pt" ? "Ver Credencial" : "View Credential"}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {totalRows > 2 && (
          <div className="show-more-wrapper">
            <button
              className={`show-more-btn${!hasMore ? " expanded" : ""}`}
              onClick={() => {
                if (hasMore) {
                  setVisibleRows((prev) => prev + ROWS_STEP);
                } else {
                  setVisibleRows(2);
                  document.getElementById("certificates")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <span>
                {hasMore
                  ? lang === "pt"
                    ? "Ver Mais"
                    : "Show More"
                  : lang === "pt"
                  ? "Ver Menos"
                  : "Show Less"}
              </span>
              <i className="fa-solid fa-chevron-down" />
            </button>
          </div>
        )}

        {/* Lightbox */}
        {lightboxUrl && (
          <div
            className="cert-lightbox-overlay"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              className="cert-lightbox-close"
              onClick={() => setLightboxUrl(null)}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            {lightboxIsPdf ? (
              <iframe
                src={lightboxUrl}
                title="Certificate PDF"
                className="cert-lightbox-pdf"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={lightboxUrl}
                alt="Certificate"
                className="cert-lightbox-img"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Certificates;
